<template>
  <div>
    <md-tabs
      class="c-tabs c-tabs--small language-picker"
      :md-active-tab="selectedLanguage"
    >
      <md-tab
        v-for="language in languages"
        :id="language"
        :key="`metatags-${language}`"
        :md-label="language"
        @click="changeLanguage(language)"
      />
    </md-tabs>

    <div class="clear-language-row">
      <span
        v-if="isSelectedLanguageEmpty"
        class="language-empty-indicator"
      >
        <md-icon>info</md-icon>
        All fields are empty. These meta tags will be ignored.
      </span>
      <md-button
        v-if="!disabled"
        class="md-dense md-primary clear-language-button"
        :disabled="isSelectedLanguageEmpty"
        @click="clearLanguage(selectedLanguage)"
      >
        <md-icon>clear</md-icon>
        Clear
      </md-button>
    </div>

    <div
      v-for="field in fields"
      :key="field.name"
      class="input-wrapper"
    >
      <label>{{ field.label }}</label>
      <input
        :value="getValue(selectedLanguage, field.key)"
        :name="field.key"
        type="text"
        :disabled="disabled"
        @input="setValue(selectedLanguage, field.key, $event)"
        @blur="trimValue(selectedLanguage, field.key, $event)"
      >
    </div>
  </div>
</template>

<script>
import {get, setWith} from 'lodash';

export default {
  name: 'MetaTagsPerLanguageInput',

  props: {
    inputValue: {
      type: Object,
      required: true
    },
    languages: {
      type: Array,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
  },

  data() {
    return {
      fields: [
        {key: 'ogTitle', label: 'Og Title'},
        {key: 'ogDescription', label: 'Og Description'},
        {key: 'ogType', label: 'Og Type'},
        {key: 'ogSiteName', label: 'Og Site Name'},
        {key: 'ogImage', label: 'Og Image (Link to image)'},
        {key: 'ogImageWidth', label: 'Og Image Width'},
        {key: 'ogImageHeight', label: 'Og Image Height'},
        {key: 'ogUrl', label: 'Og URL'},
        {key: 'ogTwitterCard', label: 'Og Twitter Card'},
      ],
      selectedLanguage: this.languages[0],
    }
  },

  computed: {
    isSelectedLanguageEmpty() {
      const languageValues = get(this.inputValue, this.selectedLanguage, {});
      return Object.values(languageValues).every(
        (value) => value === null || value === undefined || value === '',
      );
    },
  },

  methods: {
    changeLanguage(language) {
      this.selectedLanguage = language
    },

    clearLanguage(language) {
      const newVal = JSON.parse(JSON.stringify(this.inputValue));
      newVal[language] = {};
      this.$emit('update', newVal);
    },

    getValue(language, input) {
      return get(
          this.inputValue,
          `${ language }.${ input }`,
          '',
      );
    },

    async setValue(language, input, event) {
      const newVal = JSON.parse(JSON.stringify(this.inputValue));
      setWith(newVal, `${ language }.${ input }`, event.target.value, Object);

      this.$emit('update', newVal);
    },

    trimValue(language, input, event) {
      const trimmed = event.target.value.trim();
      if (trimmed === event.target.value) {
        return;
      }
      const newVal = JSON.parse(JSON.stringify(this.inputValue));
      setWith(newVal, `${ language }.${ input }`, trimmed, Object);

      this.$emit('update', newVal);
    },
  },
}

</script>
<style lang="scss" scoped>
// Same look as the standard md-field inputs: label above, outlined 40px box, brand focus ring.
.language-picker {
  margin-bottom: 16px;
}

.clear-language-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  margin-bottom: 16px;
  font-family: var(--font-sans);
}

.language-empty-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--warning-fg);
  font-size: 13px;

  .md-icon {
    color: var(--warning-fg) !important;
    font-size: 18px !important;
  }
}

// "Clear" stays on the right, also when there is no empty-state message
.clear-language-button {
  flex-shrink: 0;
  margin: 0 0 0 auto !important;
  border-radius: var(--radius-md) !important;
  text-transform: none !important;
  font-family: var(--font-sans);
  font-weight: 500;

  .md-icon {
    font-size: 18px !important;
  }
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
  font-family: var(--font-sans);

  &:last-child {
    margin-bottom: 0;
  }

  label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    line-height: 18px;
    color: var(--text-primary);
  }

  input {
    width: 100%;
    height: 40px;
    padding: 0 12px;
    border: 1px solid var(--border-strong);
    border-radius: var(--radius-md);
    outline: 0;
    background-color: var(--bg-surface);
    box-shadow: var(--shadow-xs);
    color: var(--text-primary);
    font-family: var(--font-sans);
    font-size: 14px;
    transition: border-color var(--duration-fast) ease, box-shadow var(--duration-fast) ease;

    &:hover {
      border-color: var(--text-muted);
    }

    &:focus {
      border-color: var(--brand-600);
      box-shadow: var(--focus-ring);
    }

    &:disabled {
      background-color: var(--bg-subtle);
      color: var(--text-secondary);
      cursor: not-allowed;
    }
  }
}
</style>
